/**
 * Investor-Portal — Segment INBOUND & RETURN (#1178 v2.564.0).
 *
 * Standort-getrennte Deckungsbeitrags-Analyse (AT vs DE) auf Basis Odoo
 * Kostenstellenbuchungen (account.analytic.line) — KSt 59 + 60 + 3.
 *
 * Befund 2026 YTD: Umsatz +94.967 €, DB +38.234 € (40,3 % Marge).
 *
 * Layout:
 *   - PeriodToggle
 *   - 4 KPI-Tiles (Umsatz / Aufwand / DB / DB-Marge)
 *   - 5720-Reconciliation-Indikator
 *   - AT/DE/Σ-Tabelle
 *   - Stück-KPIs: Umsatz/Paket-Annahme, /Palette, /SKU (Tooltip: Sonder-DL im Umsatz)
 *   - Retoure-Card (PRB + FDR, Umsatz + Menge + Quote)
 *   - Klumpenrisiko Top-5
 *   - Monats-Trend Jahres-Chart
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Wallet, BarChart3, Percent } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtEurDetail(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
}
function fmtEurCent(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return `${v.toFixed(1)} %`;
}
function fmtInt(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(v);
}

export default function InvestorSegmentInbound() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [yearTrend, setYearTrend] = useState(null);
  const [yearTrendLoading, setYearTrendLoading] = useState(true);
  const [yearTrendError, setYearTrendError] = useState(null);

  // #1184 v2.569.0 — YTD frontend-seitig auf belastbaren Cutoff verkürzen
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/inbound?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); }
        else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setYearTrendLoading(true);
      setYearTrendError(null);
      try {
        const res = await fetch(`${API}/api/investor/segmente/inbound/jahrestrend?year=${periodState.year}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setYearTrendError(json.error || `HTTP ${res.status}`); setYearTrend(null); }
        else setYearTrend(json);
      } catch (e) { if (!abort) setYearTrendError(e.message); }
      finally { if (!abort) setYearTrendLoading(false); }
    }
    load();
    return () => { abort = true; };
  }, [periodState.year]);

  const kpis = useMemo(() => {
    const gesamt = data?.standorte?.find(s => s.standort === 'GESAMT');
    if (!gesamt) return { umsatz: null, aufwand: null, db: null, marge: null };
    const aufwand = (gesamt.aufwand_lohn || 0) + (gesamt.aufwand_direkt || 0) + (gesamt.aufwand_revshare || 0);
    return {
      umsatz: gesamt.umsatz, aufwand,
      db: gesamt.db, marge: gesamt.db_marge_pct,
    };
  }, [data]);

  const chartData = useMemo(() => {
    if (!yearTrend?.monats_trend) return [];
    return yearTrend.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1],
      AT: Math.round(m.at_db || 0),
      DE: Math.round(m.de_db || 0),
      Gesamt: Math.round(m.db || 0),
    }));
  }, [yearTrend]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Segment Inbound &amp; Return</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Deckungsbeitrag standort-getrennt — Odoo-Kostenstellen „Inbound", „Allgemein/Lager", „Return".
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          Fehler: {error}
        </div>
      )}

      {data?.de_db_warning && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          DE-DB ist negativ — Validierungs-Anker verletzt. Prüfung der RevShare-Buchungen empfohlen.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Umsatz" value={fmtEur(kpis.umsatz)} sub="Inbound + Allgemein/Lager + Return" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD" />
        <KpiTile label="Aufwand" value={fmtEur(kpis.aufwand)} sub="Lohn + Direkt + RevShare" loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2" />
        <KpiTile label="Deckungsbeitrag" value={fmtEur(kpis.db)} sub="Umsatz − Aufwand" loading={loading}
          Icon={BarChart3} iconColor="#10B981" iconTint="#D1FAE5" />
        <KpiTile label="DB-Marge" value={fmtPct(kpis.marge)} sub="DB ÷ Umsatz" loading={loading}
          Icon={Percent} iconColor="#6366F1" iconTint="#E0E7FF" />
      </div>

      {data?.reconciliation && (
        <div className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-2 ${
          data.reconciliation.ok === true ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : data.reconciliation.ok === false ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'
        }`}>
          <span className={`w-2 h-2 rounded-full ${
            data.reconciliation.ok === true ? 'bg-emerald-500'
            : data.reconciliation.ok === false ? 'bg-amber-500' : 'bg-gray-400'
          }`} />
          {data.reconciliation.ok === true && (
            <span>GuV-Reconciliation OK — DE-RevShare ({fmtEurCent(data.reconciliation.actual)}) deckt sich mit Konto 5720 auf Inbound-Kostenstellen.</span>
          )}
          {data.reconciliation.ok === false && (
            <span>GuV-Reconciliation Abweichung: erwartet {fmtEurCent(data.reconciliation.expected)} · aktuell {fmtEurCent(data.reconciliation.actual)} · Δ {fmtEurCent(data.reconciliation.diff)}</span>
          )}
          {data.reconciliation.ok === null && (
            <span>Reconciliation nicht verfügbar{data.reconciliation.error ? ` (${data.reconciliation.error})` : ''}.</span>
          )}
        </div>
      )}

      {/* Standort-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Standort-Split</h2>
          {data && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.lines_processed} Odoo-Buchungen · {data.period?.dateFrom} – {data.period?.dateTo}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Standort</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Lohn</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Direkt</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">RevShare</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">DB</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Marge</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#9CA3AF]">Lade Odoo-Daten…</td></tr>
              )}
              {!loading && data?.standorte?.map(s => (
                <tr key={s.standort}
                  className={`border-t border-[#F3F4F6] ${s.standort === 'GESAMT' ? 'bg-[#F8F9FC] font-semibold' : 'hover:bg-[#FAFBFC]'}`}>
                  <td className="px-3 py-2 text-[#1A1A2E]">{labelFor(s.standort)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_lohn)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_direkt)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_revshare)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(s.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.db_marge_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stück-KPIs Inbound */}
      {data?.stueck_kpis && !data.stueck_kpis.error && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Stück-Umsatz Inbound</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Umsatzbasis enthält Sonder-Dienstleistungen (F / FT / FST · {fmtEur(data.stueck_kpis.sonder_umsatz)}),
              Mengen-Bezugsgröße sind die Inbound-Haupt-Produkte (PPK Paket-Annahme, PPL Palette, PWE-SKU).
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <StueckKpiTile
              label="Umsatz / Paket-Annahme"
              value={fmtEurCent(data.stueck_kpis.umsatz_per_paket_annahme)}
              sub={`${fmtInt(data.stueck_kpis.paket_annahme_menge)} Pakete (PPK)`}
            />
            <StueckKpiTile
              label="Umsatz / Palette"
              value={fmtEurCent(data.stueck_kpis.umsatz_per_palette)}
              sub={`${fmtInt(data.stueck_kpis.palette_menge)} Paletten (PPL)`}
            />
            <StueckKpiTile
              label="Umsatz / SKU"
              value={fmtEurCent(data.stueck_kpis.umsatz_per_sku)}
              sub={`${fmtInt(data.stueck_kpis.sku_menge)} SKUs (PWE-SKU)`}
            />
          </div>
        </div>
      )}

      {/* Retoure-Card */}
      {data?.return_card && !data.return_card.error && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Retoure-Bearbeitung</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Produkte PRB (Retoure-Bearbeitung) + FDR (Falschdeklaration). Quote = Retouren ÷ Paket-Annahme (Inbound-Bezug).
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StueckKpiTile label="Umsatz Retoure" value={fmtEur(data.return_card.umsatz)} sub="PRB + FDR Σ" />
            <StueckKpiTile label="Stück Retoure" value={fmtInt(data.return_card.menge)} sub="Retoure-Bearbeitungen" />
            <StueckKpiTile label="Quote Inbound" value={fmtPct(data.return_card.quote_inbound_pct)}
              sub={`Bezug ${fmtInt(data.return_card.bezugsmenge_inbound)} Paket-Annahmen`} />
            <StueckKpiTile label="AT / DE Stück"
              value={`${fmtInt(data.return_card.AT?.menge)} / ${fmtInt(data.return_card.DE?.menge)}`}
              sub={`AT ${fmtEur(data.return_card.AT?.umsatz)} · DE ${fmtEur(data.return_card.DE?.umsatz)}`} />
          </div>
        </div>
      )}

      {/* Klumpenrisiko Top-5 */}
      {data?.klumpenrisiko && !data.klumpenrisiko.error && data.klumpenrisiko.top5?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Klumpenrisiko Top-5 Kunden</h2>
            <span className="text-[11px] text-[#9CA3AF]">
              Top-3-Anteil {fmtPct(data.klumpenrisiko.top3_anteil_pct)} · {data.klumpenrisiko.kunden_count} Kunden gesamt
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Kunde</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {data.klumpenrisiko.top5.map((r, i) => (
                  <tr key={r.partner_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                    <td className="px-3 py-2 text-[#9CA3AF]">{i + 1}</td>
                    <td className="px-3 py-2 text-[#1A1A2E]">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(r.umsatz)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(r.anteil_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monats-Trend */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Trend Deckungsbeitrag {periodState.year}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Nur Monate mit Inbound-Buchungen. AT (eigenes Personal), DE (Finecom-RevShare), Σ.
            </div>
          </div>
          {yearTrendError && <span className="text-[11px] text-red-600">Fehler: {yearTrendError}</span>}
        </div>
        <div className="p-4">
          {yearTrendLoading && !yearTrend && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Jahres-Trend…</div>
          )}
          {!yearTrendLoading && chartData.length === 0 && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Daten für {periodState.year}.</div>
          )}
          {chartData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {chartData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                  <Tooltip formatter={v => fmtEurDetail(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Vorläufige Monate markieren */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Line type="monotone" dataKey="AT" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="DE" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Gesamt" stroke="#6366F1" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo <code>account.analytic.line</code> · Kostenstellen Inbound (59), Allgemein/Lager (60), Return (3) ·
        Standort-Zuordnung via <code>kunden.standort_id</code>. Finecom-Lieferantenrechnungen werden als DE-RevShare klassifiziert.
        Mapping konfigurierbar in <code>investor_segment_mapping</code>.
      </div>
    </div>
  );
}

function labelFor(s) {
  if (s === 'AT') return 'Österreich';
  if (s === 'DE') return 'Deutschland';
  if (s === 'GESAMT') return 'Gesamt';
  return s;
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function StueckKpiTile({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}
